import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../core/button';
import { Input } from '../../../core/input';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant, ExerciseSubmission } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type { MultipleChoiceEvaluationDetail } from '@org/utils-client';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';

interface MultipleChoiceExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

export function MultipleChoiceExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: MultipleChoiceExerciseProps) {
  const options = content.options ?? [];

  // Determine if this is a single-select exercise (only 1 correct answer)
  const isSingleSelect = useMemo(() => {
    const correctCount = options.filter((option) => option.isCorrect).length;
    return correctCount === 1;
  }, [options]);

  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.MultipleChoice) {
      return submission as {
        selectedOptionIds?: string[];
        selectedOptionIndices?: number[];
        selectedOptionId?: string | null;
        selectedOptionIndex?: number | null;
      };
    }
    return null;
  }, [content.lastSubmission]);

  const initialSelection = useMemo(() => {
    if (!lastSubmission) {
      return [];
    }

    const selectionSet = new Set<number>();
    const addSelection = (index: number | null | undefined) => {
      if (
        typeof index === 'number' &&
        Number.isInteger(index) &&
        index >= 0 &&
        index < options.length &&
        !selectionSet.has(index)
      ) {
        selectionSet.add(index);
      }
    };

    (lastSubmission.selectedOptionIndices ?? []).forEach(addSelection);
    addSelection(lastSubmission.selectedOptionIndex);

    const optionIds = [
      ...(lastSubmission.selectedOptionIds ?? []),
      lastSubmission.selectedOptionId ?? undefined,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);

    optionIds.forEach((optionId) => {
      const matchIndex = options.findIndex((option) => option.optionId === optionId);
      if (matchIndex >= 0) {
        addSelection(matchIndex);
      }
    });

    return Array.from(selectionSet).sort((a, b) => a - b);
  }, [lastSubmission, options]);

  const autoEvaluationVariant = useMemo(() => {
    const autoFeedback = feedback?.find((message) => message.source === 'auto');
    return autoFeedback?.variant ?? null;
  }, [feedback]);

  type OptionEvaluation = {
    index: number;
    optionId?: string;
    label?: string;
    isCorrectAnswer: boolean;
    isSelected: boolean;
    isCorrectSelection: boolean;
  };

  type MultipleChoiceDetail = MultipleChoiceEvaluationDetail & {
    optionEvaluations?: OptionEvaluation[];
  };

  const evaluationDetails = useMemo(() => {
    const evaluationPayload =
      (content as StudentExerciseContent & { lastEvaluation?: Record<string, unknown> | null }).lastEvaluation as
        | { auto?: { details?: MultipleChoiceDetail } }
        | null
        | undefined;
    const detail = evaluationPayload?.auto?.details;
    if (detail && detail.kind === 'MultipleChoice') {
      return detail;
    }
    return null;
  }, [content]);

  const optionEvaluationMap = useMemo(() => {
    if (!evaluationDetails?.optionEvaluations) {
      return null;
    }
    const map = new Map<number, OptionEvaluation>();
    evaluationDetails.optionEvaluations.forEach((option) => {
      map.set(option.index, option);
    });
    return map;
  }, [evaluationDetails]);

  const [selectedOptions, setSelectedOptions] = useState<number[]>(initialSelection);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedSet = useMemo(() => new Set(selectedOptions), [selectedOptions]);

  useEffect(() => {
    setSelectedOptions(initialSelection);
  }, [content.id, initialSelection]);

  const toggleOption = (index: number) => {
    if (isReadOnly) {
      return;
    }
    setSelectedOptions((previous) => {
      if (isSingleSelect) {
        // Single-select mode: clicking the same option deselects it, otherwise select only the new one
        if (previous.includes(index)) {
          return [];
        }
        return [index];
      }
      // Multi-select mode: toggle the option
      if (previous.includes(index)) {
        return previous.filter((value) => value !== index);
      }
      return [...previous, index].sort((a, b) => a - b);
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      const selectedOptionIds = selectedOptions
        .map((index) => options[index]?.optionId)
        .filter((value): value is string => typeof value === 'string' && value.length > 0);
      await onExerciseSubmit(
        content,
        {
          type: ExerciseType.MultipleChoice,
          selectedOptionIndices: selectedOptions,
          selectedOptionIds,
        } as ExerciseSubmission,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-4',
        variant === 'card' && 'rounded-lg border border-border bg-card p-4 shadow-sm'
      )}
    >
      {content.title && (
        <h3 className="text-lg font-semibold">
          {renderInlineMarkdown(content.title, 'multiple-choice-title')}
        </h3>
      )}
      <ContentDescription text={content.description} markdown />
      {content.question && (
        <p className="text-base font-medium whitespace-pre-line">
          {renderInlineMarkdown(content.question, 'multiple-choice-question')}
        </p>
      )}
      <div className="space-y-3" role="group" aria-disabled={isReadOnly}>
        {options.map((option, index) => {
          const optionId = option.optionId ?? `${content.id}-option-${index}`;
          const isSelected = selectedSet.has(index);
          const optionEvaluation = optionEvaluationMap?.get(index);
          let intentClass =
            'border-border bg-background hover:border-emerald-300 hover:bg-emerald-50/30';

          if (optionEvaluation) {
            if (optionEvaluation.isSelected && optionEvaluation.isCorrectSelection) {
              intentClass =
                'border-emerald-500 bg-emerald-50 text-emerald-900 shadow-sm ring-2 ring-emerald-200';
            } else if (optionEvaluation.isSelected && !optionEvaluation.isCorrectSelection) {
              intentClass =
                'border-destructive/50 bg-destructive/10 text-destructive shadow-sm ring-2 ring-destructive/30';
            } else if (!optionEvaluation.isSelected && optionEvaluation.isCorrectAnswer) {
              intentClass =
                'border-emerald-400 bg-emerald-25 text-emerald-900 shadow-sm ring-2 ring-emerald-200';
            }
          } else if (isSelected && autoEvaluationVariant) {
            if (autoEvaluationVariant === 'success') {
              intentClass =
                'border-emerald-500 bg-emerald-50 text-emerald-900 shadow-sm ring-2 ring-emerald-200';
            } else if (autoEvaluationVariant === 'warning') {
              intentClass =
                'border-amber-300 bg-amber-50 text-amber-900 shadow-sm ring-2 ring-amber-200';
            } else if (autoEvaluationVariant === 'destructive') {
              intentClass =
                'border-destructive/50 bg-destructive/10 text-destructive shadow-sm ring-2 ring-destructive/30';
            }
          } else if (isSelected) {
          intentClass =
            'border-border/80 bg-muted text-slate-900 shadow-sm ring-1 ring-border/40';
          }

          const checkboxBaseClass =
            'text-[0] focus-visible:ring-offset-0 focus-visible:ring-2 focus-visible:ring-emerald-400';
          let checkboxStateClass = isSelected
            ? 'border-emerald-500 bg-emerald-50'
            : 'border-slate-300 bg-white';

          if (optionEvaluation) {
            if (optionEvaluation.isSelected && optionEvaluation.isCorrectSelection) {
              checkboxStateClass = 'border-emerald-500 bg-emerald-50';
            } else if (optionEvaluation.isSelected && !optionEvaluation.isCorrectSelection) {
              checkboxStateClass = 'border-destructive/70 bg-destructive/10';
            } else if (!optionEvaluation.isSelected && optionEvaluation.isCorrectAnswer) {
              checkboxStateClass = 'border-emerald-400 bg-emerald-25';
            } else {
              checkboxStateClass = 'border-slate-300 bg-white';
            }
          } else if (autoEvaluationVariant === 'warning' && !isSelected && option.isCorrect) {
            checkboxStateClass = 'border-emerald-400 bg-emerald-25';
          }

          return (
            <label
              key={optionId}
              htmlFor={`${optionId}-checkbox`}
              className={cn(
                'group flex cursor-pointer items-center gap-3 rounded-md border p-3 transition',
                intentClass,
                isReadOnly && 'cursor-default opacity-80',
              )}
            >
              <Input
                id={`${optionId}-checkbox`}
                type="checkbox"
                role="checkbox"
                aria-checked={isSelected}
                aria-label={`Selecionar ${option.label || `opção ${index + 1}`}`}
                checked={isSelected}
                disabled={isReadOnly}
                onChange={() => toggleOption(index)}
                className={`${checkboxBaseClass} ${checkboxStateClass}`}
              />
              <span className="flex-1 text-sm leading-relaxed">
                {renderInlineMarkdown(
                  option.label || `Option ${index + 1}`,
                  `${content.id}-option-${index}`,
                )}
              </span>
            </label>
          );
        })}
      </div>
      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />
      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={selectedOptions.length === 0 || isSubmitting}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto"
          >
            Submit answer
          </Button>
        </div>
      )}
    </form>
  );
}
